/**
 * Incoterms Section
 * Incoterms selection with info box
 */

import { Select } from '@/components/base/select/select';
import { InfoCircle } from '@untitledui/icons';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';
import { useIncoterms } from '@/hooks/api/use-incoterms';

interface IncotermsSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    incotermsOptions: SelectOption[];
}

export function IncotermsSection({
    formData,
    onInputChange,
    incotermsOptions,
}: IncotermsSectionProps) {
    const { t } = useTranslation('pages:containers');
    const { data: incotermsData } = useIncoterms();

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.incoterms', 'Incoterms')}</h2>
            <Select
                label={t('form.fields.incoterms', 'Incoterms')}
                placeholder={t('form.fields.incotermsPlaceholder', 'Select Incoterm...')}
                items={incotermsOptions}
                selectedKey={formData.incoterms}
                onSelectionChange={(key) => onInputChange('incoterms', key as string)}
                size="md"
            >
                {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                        <div className="flex items-center justify-between w-full">
                            <span>{item.label}</span>
                            {item.description && (
                                <span className="text-xs text-tertiary ml-2 truncate max-w-[200px]">
                                    {item.description}
                                </span>
                            )}
                        </div>
                    </Select.Item>
                )}
            </Select>
            {formData.incoterms && incotermsData?.incoterms && (
                <div className="mt-3 p-3 rounded-lg bg-secondary text-sm">
                    <div className="flex items-start gap-2">
                        <InfoCircle className="size-4 text-tertiary mt-0.5" />
                        <div>
                            <strong className="text-primary">{incotermsData.incoterms.find(i => i.code === formData.incoterms)?.name}</strong>
                            <p className="text-tertiary mt-1">
                                {incotermsData.incoterms.find(i => i.code === formData.incoterms)?.risk_transfer_point}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
